<template>
  <!-- Sliding panel -->
  <SlidingPanel :isPanelOpen="slidingPanelIsOpen" @closePanel="closePanel" :isSubPanelOpen="false">
    <template #content>
      <CampaignConfigPanel v-if="currentPanel === BASIC_INFO_PANEL" :campaign="props.modelValue" @close="closePanel" @save="save"></CampaignConfigPanel>
      <EnvironmentPanel v-else-if="currentPanel === ENVIRONMENT_PANEL" :campaign="props.modelValue" @close="closePanel" @save="save"></EnvironmentPanel>
      <EvaluationConfigPanel v-else-if="currentPanel === EVALUATION_PANEL" :campaign="props.modelValue" @close="closePanel" @save="save" :disableIgnored="false" :footerActivated="true"></EvaluationConfigPanel>
      <OptionsConfigPanel v-else-if="currentPanel === OPTIONS_PANEL" :campaign="props.modelValue" @save="saveWithoutRunningYuzuAI" @close="closePanel" @openCustomizationPanel="openPanel(CAMPAIGN_CARD_PANEL)"></OptionsConfigPanel>
    </template>
  </SlidingPanel>
  <div class="field-wrapper">
    <div class="section campaign-recap">
      <div class="section-title">
        <p>{{ $t('app.campaign.newPage.recap.infoSection.title') }}</p>
        <Button icon="bi-pencil" class="icon icon-left sm outline-white" @click="openPanel(BASIC_INFO_PANEL)" :label="$t('other.actions.edit')"></Button>
      </div>
      <div class="campaign-recap-body">
        <div>
          <p class="light-header">{{ $t('app.campaign.newPage.chooseSpace.label') }}</p>
          <p class="text">{{ spaceName }}</p>
        </div>
        <div>
          <p class="light-header">{{ $t('app.campaign.newPage.campaignName') }}</p>
          <p class="text">{{ props.modelValue.campaign.internal_name }}</p>
        </div>
        <div>
          <p class="light-header">{{ $t('app.campaign.newPage.confirmJob.title') }}</p>
          <p class="text">{{ !props.modelValue.campaign.job_title.length ? $t('app.campaign.newPage.noJob') :  props.modelValue.campaign.job_title }}</p>
        </div>
        <div>
          <p class="light-header">{{ $t('app.campaign.newPage.recap.environmentSection.title') }}</p>
          <p class="text">{{ $t(`app.campaign.newPage.slidingPanel.environment.${props.modelValue.assessment.environment}.title`) }}</p>
        </div>
      </div>
    </div>
    <div class="divider"></div>
    <div class="section evaluation-informations">
      <div class="section-title">
        <div class="inline">
          <p>{{ $t('app.campaign.newPage.recap.evaluationSection.title') }}</p>
          <div class="yuzu-icon yuzu-ai"></div>
        </div>
        <Button icon="bi-pencil" class="icon icon-left sm outline-white" @click="openPanel(EVALUATION_PANEL)" :label="$t('other.actions.edit')"></Button>
      </div>
      <WeightingsRecap
        :allModules="props.modelValue.assessment.modules"
        :requiredSkills="requiredSkillsLabels"
        :importantSkills="importantSkillsLabels"
        :secondarySkills="secondarySkillsLabels"
        :ignoredSkills="ignoredSkillsLabels"
        :campaignVersion="props?.modelValue?.campaign_version"
      >
      </WeightingsRecap>
      <p class="text" style="margin-top: 8px;">{{ $t('app.campaign.newPage.recap.evaluationSection.adventureDurationText') }}&nbsp;<span class="bold">{{ skillsStore.getAdventureDuration(props?.modelValue?.assessment?.modules || []) }} minutes</span></p>
    </div>
    <div class="divider"></div>
    <div class="section additional-options">
      <div class="section-title">
        <p>{{ $t('app.campaign.newPage.recap.optionsSection.title') }}</p>
        <Button icon="bi-pencil" class="icon icon-left sm outline-white" @click="openPanel(OPTIONS_PANEL)" :label="$t('other.actions.edit')"></Button>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@import "@/assets/style/variables.scss";

.inline {
  display: flex;
  gap: 8px;
}

.field-wrapper {
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 20px;

  .section {
    .section-title {
      display: flex;
      justify-content: space-between;
      align-items: center;
      gap: 10px;
      margin-bottom: 16px;

      p {
        color: var(--secondary-color);
        font-family: $bioSans;
        font-size: 15px;
        font-style: normal;
        font-weight: 700;
        line-height: normal;
      }

      &:last-of-type {
        margin-bottom: 0;
      }
    }

    .light-header {
      color: $secondary-100-color;
      font-family: $bioSans;
      font-size: 12px;
      font-style: normal;
      font-weight: 600;
      line-height: 18px; /* 150% */
    }

    .text {
      font-size: 12px;
      font-weight: 600;
    }

    .campaign-recap-body {
      display: grid;
      grid-template-columns: 1fr 1fr 1fr;
      row-gap: 16px;

      padding: 16px;
      border-radius: 4px;
      background-color: var(--light-white-color);
      border: 1px solid var(--light-grey-color);
    }
  }

  .divider {
    height: 1px;
    background-color: var(--light-grey-color);
    width: 100%;
  }
}
</style>

<script setup lang="ts">
import { cloneDeep } from "lodash";

import SlidingPanel from '@/components/sliding-panel/SlidingPanel.vue';
import CampaignConfigPanel from '@/components/sliding-panel/campaignCreation/CampaignConfigPanel.vue';
import EnvironmentPanel from '@/components/sliding-panel/campaignCreation/EnvironmentPanel.vue';
import EvaluationConfigPanel from '@/components/sliding-panel/campaign/EvaluationConfigPanel.vue';
import OptionsConfigPanel from '@/components/sliding-panel/campaignCreation/OptionsConfigPanel.vue';
import WeightingsRecap from "@/components/campaigns/weighting/WeightingsRecap.vue";
import Button from "@/components/global/Button.vue";

import GearIcon from '@/assets/images/icon/campaign/gear.svg?component';

import { CampaignCreation, WeightingModule } from '@/types/campaign';
import { useSpaces } from "@/queries/space.query";

const BASIC_INFO_PANEL = 0;
const ENVIRONMENT_PANEL = 1;
const EVALUATION_PANEL = 2;
const OPTIONS_PANEL = 3;
const CAMPAIGN_CARD_PANEL = 4;

const currentPanel = ref(BASIC_INFO_PANEL);

const slidingPanelIsOpen = ref(false);

const route = useRoute();
const skillsStore = useSkillsStore();
const { skills } = useSkillsStore();

const { t } = useI18n();

const { data: spaces, isPending: isSpacesPending } = useSpaces();

const props = defineProps<{
  modelValue: CampaignCreation,
}>();

const emit = defineEmits<{
  (e: 'update:modelValue', value: CampaignCreation): void
  (e: 'valid', value: boolean): void
  (e: 'runYuzuAi'): void
  (e: 'configPanelIsOpen', value: boolean): void
}>();

const spaceName = computed(() => {
  if (isSpacesPending.value) return "";
  const space = spaces.value.find(space => space.workspace_id === props.modelValue.campaign.parent_workspace_id);
  return space?.name || "";
});

const requiredSkillsLabels = computed(() => {
  if (typeof props.modelValue.assessment.weighting === 'string') return [];
  return props.modelValue.assessment.weighting?.required || [];
});

const importantSkillsLabels = computed(() => {
  if (typeof props.modelValue.assessment.weighting === 'string') return [];
  const important = cloneDeep(props.modelValue.assessment.weighting?.primary || []);
  return important;
});

const secondarySkillsLabels = computed(() => {
  if (typeof props.modelValue.assessment.weighting === 'string') return [];
  return props.modelValue.assessment.weighting?.secondary || [];
});

const ignoredSkillsLabels = computed(() => {
  if (typeof props.modelValue.assessment.weighting === 'string') return [];
  const ignored = cloneDeep(props.modelValue.assessment.weighting?.ignored || []);
  return ignored;
});

function openPanel(panel: number) {
  currentPanel.value = panel;
  slidingPanelIsOpen.value = true;
  emit('configPanelIsOpen', true);
}

function closePanel() {
  slidingPanelIsOpen.value = false;
  emit('configPanelIsOpen', false);
}


function saveWithoutRunningYuzuAI(value: CampaignCreation) {
  emit('update:modelValue', value);
}

function save(value: CampaignCreation) {
  if (value.campaign.job_title.trim() !== props.modelValue.campaign.job_title.trim()) {
    emit('update:modelValue', value);
    emit('runYuzuAi');
  } else {
    emit('update:modelValue', value);
  }
}

</script>